import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from './components/DataTable/DataTable';
import { CITIES_COLUMNS, getStateForCity, getPlatformFromUrl } from './constants/tableColumns';
import API_BASE_URL from './config';

/**
 * CitiesTableView - Refactored Cities table using DataTable component
 * 
 * Sprint 20: Replaces 800+ lines of custom filter/sort/table code with DataTable
 */

export default function CitiesTableView({
  userRole,
  isAdmin,
  onCitySelect,
  theme,
}) {
  const navigate = useNavigate();
  
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowSelection, setRowSelection] = useState({}); // Track selected rows by index
  const [showAddSection, setShowAddSection] = useState(false);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        // Admin: Show ALL cities (scraper database)
        const response = await fetch(`${API_BASE_URL}/api/cities`, { credentials: 'include' });
        const data = await response.json();
        setCities(data);
      } else {
        // Regular user: Show only company's tracked cities
        const [companyCitiesResponse, allCitiesResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/company/cities`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/cities`, { credentials: 'include' })
        ]);
        
        const companyCities = await companyCitiesResponse.json();
        const allCities = await allCitiesResponse.json();
        
        // Filter to only show cities tracked by company
        const trackedCityNames = new Set(companyCities.map(c => c.name));
        const filteredCities = allCities.filter(city => trackedCityNames.has(city.name));
        setCities(filteredCities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add computed state and platform to each city for filtering/sorting
  const citiesWithMetadata = useMemo(() => {
    return cities.map(city => ({
      ...city,
      state: getStateForCity(city.name),
      platform: getPlatformFromUrl(city.url),
    }));
  }, [cities]);

  // Prepare table columns - add onClick handler to name column
  const columns = useMemo(() => {
    return CITIES_COLUMNS.map(col => {
      if (col.id === 'name') {
        return {
          ...col,
          cell: ({ row }) => (
            <div>
              <button 
                className="btn btn-link p-0 text-decoration-none"
                style={{ textAlign: 'left' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onCitySelect(row.original.name);
                }}
              >
                {row.original.name}
              </button>
              {(row.original.manual || !row.original.is_active) && (
                <span 
                  className="badge bg-warning text-dark ms-2"
                  title="This city requires manual job entry"
                >
                  ⚠️ Manual
                </span>
              )}
            </div>
          ),
        };
      }
      return col;
    });
  }, [onCitySelect]);

  // Filter configuration for Cities table
  const filterConfig = useMemo(() => ({
    state: {
      type: 'select',
      label: 'State',
      options: [
        { value: 'AZ', label: 'Arizona' },
        { value: 'CO', label: 'Colorado' },
        { value: 'NM', label: 'New Mexico' },
        { value: 'FL', label: 'Florida' },
        { value: 'GA', label: 'Georgia' },
        { value: 'TX', label: 'Texas' },
        { value: 'OTHER', label: 'Other' },
      ],
      allLabel: `All States (${cities.length})`,
      className: 'col-md-3',
    },
    platform: {
      type: 'select',
      label: 'Platform',
      options: [
        { value: 'Bonfire', label: 'Bonfire' },
        { value: 'OpenGov', label: 'OpenGov' },
        { value: 'PlanetBids', label: 'PlanetBids' },
        { value: 'CivicPlus', label: 'CivicPlus' },
        { value: 'Other', label: 'Other' },
      ],
      allLabel: 'All Platforms',
      className: 'col-md-3',
    },
    manual: {
      type: 'checkbox',
      label: 'Show only manual cities',
      className: 'col-md-3',
    },
  }), [cities.length]);

  // Bulk actions for admin
  const handleExportCities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/export/cities`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to export cities');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cities_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Cities exported successfully!');
    } catch (error) {
      console.error('Error exporting cities:', error);
      alert('❌ Failed to export cities. Check console for details.');
    }
  };

  const handleScrapeSelected = async () => {
    // Get selected row indices and map to city names
    const selectedIndices = Object.keys(rowSelection).filter(key => rowSelection[key]);
    
    if (selectedIndices.length === 0) {
      alert('Please select cities to scrape');
      return;
    }

    // Load filters from localStorage to compute filtered data (matching DataTable's filtering)
    // This is necessary because rowSelection indices refer to the filtered table rows, not the original array
    let customFilters = {};
    try {
      const saved = localStorage.getItem('cities-table-filters');
      customFilters = saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading filters:', error);
    }
    
    // Apply same filtering logic as DataTable to get filtered array
    const filteredCities = citiesWithMetadata.filter(city => {
      for (const [key, value] of Object.entries(customFilters)) {
        if (key === 'sortBy') continue;
        if (value === 'all' || value === '' || value == null) continue;
        
        if (key === 'platform') {
          if (city.platform !== value) return false;
        } else if (key === 'state') {
          if (city.state !== value) return false;
        } else if (key === 'manual') {
          if (value === true && !city.manual) return false;
        }
      }
      return true;
    });

    // Use filtered array to map indices (rowSelection indices refer to filtered data)
    const selectedCityNames = selectedIndices.map(index => {
      const city = filteredCities[parseInt(index)];
      return city?.name;
    }).filter(Boolean);
    const confirmMsg = `Scrape ${selectedCityNames.length} selected ${selectedCityNames.length === 1 ? 'city' : 'cities'}?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/run_scraper`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ cities: selectedCityNames })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Server error:', errorData);
        throw new Error(errorData.error || errorData.detail || 'Failed to start scraping');
      }

      const data = await response.json();
      alert(`✅ ${data.message || 'Scraping started for selected cities'}`);
      
      // Clear selection after scraping
      setRowSelection({});
      
      // Refresh cities list
      fetchCities();
    } catch (error) {
      console.error('Error scraping cities:', error);
      alert(`❌ Failed to start scraping: ${error.message}`);
    }
  };

  const handleBulkUpload = async (file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_BASE_URL}/upload_cities`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.error) {
        alert(`❌ ${data.error}`);
        return;
      }
      
      // Log full details to console for history
      console.log('\n' + '='.repeat(70));
      console.log('📊 BULK CITY UPLOAD REPORT - ' + new Date().toLocaleString());
      console.log('='.repeat(70));
      console.log('Full upload details:', data);
      console.log('='.repeat(70) + '\n');
      
      // Show detailed results in alert
      let message = `✅ Bulk Upload Complete!\n\n📊 SUMMARY:\n`;
      message += `• Total Rows: ${data.summary.total_rows}\n`;
      message += `• ✅ Added: ${data.summary.added}\n`;
      message += `• ⏭️ Skipped: ${data.summary.skipped}\n`;
      message += `• ❌ Errors: ${data.summary.errors}\n`;
      message += `\n${'─'.repeat(50)}\n\n`;
      
      if (data.details.added.length > 0) {
        message += `✅ ADDED (${data.details.added.length}):\n`;
        data.details.added.forEach(item => {
          message += `  • Row ${item.row}: ${item.organization}\n`;
        });
        message += `\n`;
      }
      
      if (data.details.skipped.length > 0) {
        message += `⏭️ SKIPPED (${data.details.skipped.length}) - Already Exist:\n`;
        data.details.skipped.forEach(item => {
          message += `  • Row ${item.row}: ${item.organization}\n`;
          message += `    └─ ${item.reason}`;
          if (item.detail) {
            message += `\n    └─ ${item.detail}`;
          }
          message += `\n`;
        });
        message += `\n`;
      }
      
      if (data.details.errors.length > 0) {
        message += `❌ ERRORS (${data.details.errors.length}) - Need Fixing:\n`;
        data.details.errors.forEach(item => {
          message += `  • Row ${item.row}: ${item.organization || '(blank)'}\n`;
          message += `    └─ ${item.error}\n`;
        });
        message += `\n`;
      }
      
      message += `💡 Full details logged to browser console (F12)`;
      
      alert(message);
      
      // Refresh cities list
      fetchCities();
      
    } catch (error) {
      console.error('Error uploading cities:', error);
      alert('❌ Failed to upload file. Check console for details.');
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading cities...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '100%' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ margin: 0 }}>Cities & Organizations</h2>
        {isAdmin && (
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-primary"
              onClick={handleExportCities}
            >
              📊 Export to Excel
            </button>
                <button 
                  className="btn btn-outline-success"
                  onClick={handleScrapeSelected}
                  disabled={Object.keys(rowSelection).filter(key => rowSelection[key]).length === 0}
                >
                  🔄 Scrape Selected ({Object.keys(rowSelection).filter(key => rowSelection[key]).length})
                </button>
          </div>
        )}
      </div>

      {/* Add Cities Section - Admin Only */}
      {isAdmin && (
        <div className="card mb-3" style={{
          backgroundColor: theme === 'dark' ? '#2b3035' : '#fff',
          border: theme === 'dark' ? '1px solid #495057' : '1px solid #dee2e6',
        }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showAddSection ? '15px' : '0' }}>
              <h5 style={{ margin: 0, color: theme === 'dark' ? '#e0e0e0' : '#000' }}>
                ➕ Add Cities
              </h5>
              <button 
                className="btn btn-sm btn-link"
                onClick={() => setShowAddSection(!showAddSection)}
                style={{
                  fontSize: '20px',
                  textDecoration: 'none',
                  color: theme === 'dark' ? '#e0e0e0' : '#000'
                }}
              >
                {showAddSection ? '▼' : '▶'}
              </button>
            </div>
            
            {showAddSection && (
              <div>
                <p style={{ 
                  color: theme === 'dark' ? '#adb5bd' : '#6c757d',
                  fontSize: '14px',
                  marginBottom: '15px'
                }}>
                  Upload a CSV or Excel file with columns: <code>organization</code>, <code>url</code> (required).
                  Optional: <code>row_selector</code>, <code>cell_count</code>, <code>is_dynamic</code>, <code>manual</code>
                </p>
                
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                  <label 
                    htmlFor="bulk-upload-input" 
                    className="btn btn-primary"
                  >
                    📊 Choose File
                  </label>
                  <input 
                    id="bulk-upload-input"
                    type="file" 
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => handleBulkUpload(e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                  <span style={{ 
                    color: theme === 'dark' ? '#adb5bd' : '#6c757d',
                    fontSize: '13px'
                  }}>
                    Upload CSV or Excel (.xlsx, .xls)
                  </span>
                </div>
                
                <div style={{ 
                  padding: '10px',
                  backgroundColor: theme === 'dark' ? 'rgba(13, 110, 253, 0.15)' : '#e7f3ff',
                  borderLeft: `4px solid #0d6efd`,
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: theme === 'dark' ? '#9ec5fe' : '#084298'
                }}>
                  <strong>💡 Tip:</strong> After uploading cities, they'll appear in the table below. You can then select them and use the bulk actions.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
          <DataTable
            columns={columns}
            data={citiesWithMetadata}
            tableId="cities-table"
            enableRowSelection={isAdmin} // Only admins can select rows
            enableColumnResizing={true}
            enableColumnOrdering={true}
            enableFilters={true}
            enablePagination={false} // Show all cities like the original
            pageSize={100}
            filterConfig={filterConfig}
            onRowClick={null} // Don't make entire row clickable - only city name button
            className="cities-table"
            defaultSorting={[{ id: 'name', desc: false }]} // Sort by name ascending by default
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
          />
    </div>
  );
}

